<script>
  import { onMount } from 'svelte';
  import FulfillmentService from '../../Common/FulfillmentService';
  import { External, M } from '../../external';
  import Loader from '../../Common/Loader.svelte';
  import { getRandomInt } from '../../Common/tools';

  export let provider, onClose = () => {
  };

  let mappingKey = '';
  let selectedMappings = [];
  let selectedProduct = null;
  let selectedVariant = null;
  let selectedProviderProduct = null;
  let selectedProviderVariant = null;

  const providerProducts = {
    page: 0,
    loading: false,
    data: [],
    hasMore: false,
    nextPageToken: null
  };

  const storeProducts = {
    loading: false,
    data: []
  };

  const isAlreadyMapped = (product, variant = null) => {
    return selectedMappings.some(mapping => {
      if (variant) {
        // Check if this specific variant is mapped
        return mapping.storeProduct.id === product.id &&
          mapping.storeProduct.variant?.combination === variant.combination;
      } else {
        // Check if the product itself (without variant) is mapped
        return mapping.storeProduct.id === product.id &&
          !mapping.storeProduct.variant;
      }
    });
  };

  const doFetchProviderProducts = async () => {
    const response = await FulfillmentService.getProviderProducts(provider, providerProducts.page++, providerProducts.nextPageToken);
    if (!response.data) {
      response.data = [];
    }
    providerProducts.data = [...providerProducts.data, ...response.data];
    providerProducts.hasMore = response.hasMore;
    providerProducts.nextPageToken = response.nextPageToken;
    if (providerProducts.hasMore) {
      await doFetchProviderProducts();
    }
  };

  const fetchProviderProducts = async () => {
    providerProducts.loading = true;
    await doFetchProviderProducts();
    providerProducts.loading = false;
  };

  const fetchStoreProducts = async () => {
    storeProducts.loading = true;
    storeProducts.data = await External.Tools.getAllStoreProducts();
    storeProducts.loading = false;
  };

  const generateInitialMappings = () => {
    const newMappings = [];

    const findProviderProductAndVariant = (sku) => {
      for (const providerProduct of providerProducts.data) {
        if (providerProduct.externalId === sku) {
          return { product: providerProduct, variant: null };
        }
        // Check if provider product has a matching SKU in its variants
        if (providerProduct.variants) {
          for (const variant of providerProduct.variants) {
            if (variant.externalId === sku) {
              return {
                product: providerProduct,
                variant: variant
              };
            }
          }
        }
      }
      return null;
    };

    storeProducts.data.forEach(storeProduct => {
      // Check main product SKU
      if (storeProduct.sku) {
        const match = findProviderProductAndVariant(storeProduct.sku);
        if (match) {
          newMappings.push({
            storeProduct: {
              id: storeProduct.id,
              title: storeProduct.title,
              variant: null
            },
            providerProduct: {
              externalId: match.product.externalId,
              name: match.product.name,
              variant: match.variant ? {
                externalId: match.variant.externalId,
                name: match.variant.name
              } : null
            }
          });
        }
      }

      // Check combinations (variants) SKUs
      if (storeProduct.combinations) {
        storeProduct.combinations.forEach(combination => {
          if (combination.sku) {
            const match = findProviderProductAndVariant(combination.sku);
            if (match) {
              newMappings.push({
                storeProduct: {
                  id: storeProduct.id,
                  title: storeProduct.title,
                  variant: {
                    combination: combination.combination,
                    name: combination.name
                  }
                },
                providerProduct: {
                  externalId: match.product.externalId,
                  name: match.product.name,
                  variant: match.variant ? {
                    externalId: match.variant.externalId,
                    name: match.variant.name
                  } : null
                }
              });
            }
          }
        });
      }
    });

    selectedMappings = newMappings;
    console.log('selectedMappings', selectedMappings);
  };

  const addMapping = () => {
    if (selectedProduct && selectedProviderProduct) {
      const mapping = {
        storeProduct: {
          id: selectedProduct.id,
          title: selectedProduct.title,
          variant: selectedVariant
            ? {
              combination: selectedVariant.combination,
              name: selectedVariant.name
            }
            : null
        },
        providerProduct: {
          externalId: selectedProviderProduct.externalId,
          name: selectedProviderProduct.name,
          variant: selectedProviderVariant
            ? {
              externalId: selectedProviderVariant.externalId,
              name: selectedProviderVariant.name
            }
            : null
        }
      };

      selectedMappings = [...selectedMappings, mapping];
      mappingKey = getRandomInt();
      console.log('selectedMappings', selectedMappings);

      // Reset selections
      selectedProduct = null;
      selectedVariant = null;
      selectedProviderProduct = null;
      selectedProviderVariant = null;
    }
  };

  const removeMapping = (index) => {
    selectedMappings = selectedMappings.filter((_, i) => i !== index);
    mappingKey = getRandomInt();
    console.log('selectedMappings', selectedMappings);
  };

  let saveInProgress = false;
  const saveMappings = async () => {
    try {
      saveInProgress = true;
      const mappingsToSave = selectedMappings.map(mapping => {
        return {
          storeProductId: mapping.storeProduct.id,
          combination: mapping.storeProduct.variant?.combination ?? null,
          providerExternalId: mapping.providerProduct.variant?.externalId || mapping.providerProduct.externalId
        };
      });

      console.log('Saving mappings:', mappingsToSave);
      await External.Tools.saveProductSkus(mappingsToSave);
      saveInProgress = false;
      onClose();
    } catch (error) {
      console.error('Error saving mappings:', error);
      saveInProgress = false;
    }
  };

  onMount(async () => {
    await Promise.all([fetchProviderProducts(), fetchStoreProducts()]);
    generateInitialMappings();
  });

  $: canAddMapping = selectedProduct && selectedProviderProduct && selectedVariant !== undefined;
</script>

<div class="popup-widget-wrapper flex flex-col full-size">
  {#if providerProducts.loading || storeProducts.loading}
    <Loader clz="big" />
  {:else}
    <span class="col20 f-left t-center flex-none">{M.MATCH_PRODUCTS_DESC}</span>
    {#key mappingKey}
      <div class="provider-products col20 f-left flex-1" style="overflow-y: auto;">
        <div class="mappings-list mt-25 col20 f-left">
          {#each selectedMappings as mapping, index}
            <div class="mapping-item">
              <div class="mapping-details">
                <div class="store-product">
                  <strong>{mapping.storeProduct.title}</strong>
                  {#if mapping.storeProduct.variant}
                    <span class="variant">{mapping.storeProduct.variant.name}</span>
                  {/if}
                </div>
                →
                <div class="provider-product">
                  <strong>{mapping.providerProduct.name}</strong>
                  {#if mapping.providerProduct.variant}
                    <span class="variant">{mapping.providerProduct.variant.name}</span>
                  {/if}
                </div>
              </div>
              <button class="middle-button gray rounded-effect flex-none" on:click={() => removeMapping(index)}>{M.REMOVE}</button>
            </div>
          {/each}
        </div>
        <div class="mapping-controls mb-20 col20 f-left">
          <div class="mapping-row">
            <div class="flex flex-col flex-1 store-product-selectors">
              <select
                class="page-selector"
                bind:value={selectedProduct}
                on:change={() => {
                // Only reset variant if product actually changed
                if (selectedVariant?.productId !== selectedProduct?.id) {
                  selectedVariant = undefined;  // Use undefined instead of null to avoid pre-selecting
                }
              }}>
                <option value={null}>{M.SELECT_PRODUCTS}</option>
                {#each storeProducts.data as product}
                  <option value={product}>
                    {product.title}
                  </option>
                {/each}
              </select>

              {#if selectedProduct?.combinations}
                <select class="page-selector" bind:value={selectedVariant}>
                  <option value={undefined}>{M.SELECT_VARIANT}</option>
                  <option
                    value={null}
                    disabled={isAlreadyMapped(selectedProduct)}
                  >
                    (Product itself) {isAlreadyMapped(selectedProduct) ? `(${M.MAPPED})` : ''}
                  </option>
                  {#each selectedProduct.combinations as variant}
                    <option
                      value={variant}
                      disabled={isAlreadyMapped(selectedProduct, variant)}
                    >
                      {variant.name} {isAlreadyMapped(selectedProduct, variant) ? `(${M.MAPPED})` : ''}
                    </option>
                  {/each}
                </select>
              {/if}
            </div>

            <div class="flex flex-col flex-1 provider-product-selectors">
              <select class="page-selector" bind:value={selectedProviderProduct}>
                <option value={null}>{M.SELECT_PROVIDER_PRODUCT}</option>
                {#each providerProducts.data as product}
                  <option value={product}>{product.name}</option>
                {/each}
              </select>

              {#if selectedProviderProduct?.variants}
                <select class="page-selector" bind:value={selectedProviderVariant}>
                  <option value={null}>(Product itself)</option>
                  {#each selectedProviderProduct.variants as variant}
                    <option value={variant}>{variant.name}</option>
                  {/each}
                </select>
              {/if}
            </div>

            <button class:disabled={!canAddMapping} disabled={!canAddMapping} class="middle-button blue rounded-effect flex-none"
                    on:click={addMapping}>{M.ADD_MAPPING}</button>
          </div>
        </div>
      </div>
    {/key}
    <div class="col20 f-left mt-15 flex-none">
      <button class:disabled={saveInProgress} disabled={saveInProgress} class="middle-button blue f-right rounded-effect" on:click={saveMappings}>
        {M.SAVE_MAPPINGS}
      </button>
    </div>
  {/if}
</div>

<style>

  .mapping-row {
    display: flex;
    gap: 20px;
    align-items: center;
    margin-bottom: 10px;
  }

  .mapping-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 10px 10px 20px;
    background: #f5f6fa;
    border-radius: 15px;
    margin-bottom: 10px;
  }

  .mapping-details {
    display: flex;
    gap: 20px;
  }

  .variant {
    color: #666;
    font-size: 0.9em;
    margin-left: 8px;
  }
</style>
